import { FC, useState } from 'react';
import {
  BaseImageList,
  Button,
  Modal,
  SelectInput,
  SwitchInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { Stack } from '@mui/material';
import {
  RadioButtonChecked,
  RadioButtonUnchecked,
  ZoomOutMap,
} from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form-latest';
import {
  AspectRatio,
  GeneratedImage,
  ImageBrandColor,
  imageBrandColors,
  ImageScene,
  imageScenes,
  ImageStyle,
  imageStyles,
  useGenerateImage,
} from 'store/ai-image-generation';
import { binaries, Binary } from 'types/Common';
import { SystemColors } from 'melp-design/style';
import { dataUrlToFile } from 'utils/general';
import { ExampleFileModal, ImagePreviewModal } from './atoms';

interface Values {
  instructions: string;
  example: File | undefined;
  showOptions: boolean;
  style: ImageStyle;
  scene: ImageScene;
  brandColor: ImageBrandColor;
  people: Binary;
  selectedImage: number;
}

const INITIAL_VALUES: Values = {
  instructions: '',
  example: undefined,
  showOptions: false,
  style: 'flat_illustration',
  scene: 'abstract',
  brandColor: 'low_emphasis',
  people: 'no',
  selectedImage: NaN,
};

interface Props extends ModalProps {
  title?: string;
  description?: string;
  aspectRatio?: AspectRatio;
  closeModal: (
    param?: { action: 'CLOSE' } | { action: 'SELECT'; file: File },
  ) => void;
}

export const AiGenerationModal: FC<Props> = ({
  closeModal,
  title,
  description,
  aspectRatio = [16, 9],
}) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();

  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);

  const { control, getValues, setValue, watch } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { mutate: generateImage, isLoading } = useGenerateImage();

  const [instructions, example, showOptions, selectedImage] = watch([
    'instructions',
    'example',
    'showOptions',
    'selectedImage',
  ]);
  const onGenerate = () => {
    const values = getValues();

    generateImage(
      {
        instructions: values.instructions,
        example: values.example,
        style: values.style,
        scene: values.scene,
        brandColor: values.brandColor,
        people: values.people,
        aspectRatio,
      },
      {
        onSuccess: ({ data }) => {
          setGeneratedImages((prev) => [...prev, ...data.predictions]);
        },
      },
    );
  };

  return (
    <Modal.Content
      title={title || t('ai.image_generation.title')}
      actions={[
        {
          variant: 'neutral-outline',
          onClick: () => closeModal(),
          label: t('common.cancel'),
        },
        {
          label: generatedImages.length
            ? t('ai.actions.generate_more')
            : t('ai.actions.generate'),
          variant: 'secondary',
          onClick: () => {
            onGenerate();
          },
          disabled: isLoading || !instructions,
        },
        {
          variant: 'primary',
          label: t('ai.actions.insert'),
          onClick: async () => {
            if (Number.isNaN(selectedImage)) closeModal();

            const selected = generatedImages[selectedImage];

            if (!selected) closeModal();

            const selectedFile = await dataUrlToFile(
              `data:${selected.mimeType};base64,${selected.base64}`,
              'generated-image',
              selected.mimeType,
            );

            closeModal({
              action: 'SELECT',
              file: selectedFile,
            });
          },
          disabled: !selectedImage,
        },
      ]}
      dividers
    >
      <form>
        <Stack gap={4}>
          <Stack gap={1}>
            <Controller
              name="instructions"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <TextField
                  label={t('ai.images.instructions.label')}
                  placeholder={t('ai.images.instructions.info')}
                  rows={3}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              )}
            />
            <Stack flexDirection="row" gap={1}>
              {description ? (
                <Button
                  label={t('ai.actions.prefill')}
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setValue('instructions', description);
                  }}
                />
              ) : null}
              <Button
                label={t('ai.actions.upload_example')}
                size="sm"
                variant="neutral-outline"
                onClick={async () => {
                  const response = await openModal({
                    component: ExampleFileModal,
                    props: {
                      initialExample: example,
                    },
                  });

                  if (response.action === 'CONFIRM') {
                    setValue('example', response.example);
                  }
                }}
                badge={example ? 1 : undefined}
              />
            </Stack>
          </Stack>

          <Stack gap={2}>
            <Controller
              name="showOptions"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <SwitchInput
                  label={t('ai.options.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            {showOptions ? (
              <>
                <Controller
                  name="style"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        label={t('ai.images.style.label')}
                        options={imageStyles.map((type) => ({
                          label: t(`ai.images.style.${type}`),
                          value: type,
                        }))}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="scene"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        label={t('ai.images.scene.label')}
                        options={imageScenes.map((type) => ({
                          label: t(`ai.images.scene.${type}`),
                          value: type,
                        }))}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="people"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SelectInput
                      label={t('ai.images.people.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      options={binaries.map((binary) => ({
                        value: binary,
                        label: t(`common.${binary}`),
                      }))}
                      required
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="brandColor"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        label={t('ai.images.brand_colors.label')}
                        options={imageBrandColors.map((type) => ({
                          label: t(`ai.images.brand_colors.${type}`),
                          value: type,
                        }))}
                        required
                        ref={ref}
                      />
                    );
                  }}
                />
              </>
            ) : null}
          </Stack>

          <Stack>
            <Stack
              flexDirection="row"
              sx={{
                py: 1,
                my: 1,
                borderBottom: `1px solid ${SystemColors.grey[90]}`,
              }}
            >
              <Typography variant="h3">
                {t('ai.images.generated.title')}
              </Typography>
            </Stack>

            <BaseImageList
              items={generatedImages.map((image, i) => {
                const src = `data:${image.mimeType};base64,${image.base64}`;
                const isSelected = selectedImage === i;

                return {
                  src,
                  state: isSelected ? 'active' : 'faded',
                  actions: [
                    {
                      label: t('status.selected'),
                      icon: isSelected
                        ? RadioButtonChecked
                        : RadioButtonUnchecked,
                      variant: 'neutral-outline',
                      onClick: () => {
                        setValue('selectedImage', i);
                      },
                    },
                    {
                      icon: ZoomOutMap,
                      variant: 'neutral-outline',
                      onClick: async () => {
                        const response = await openModal({
                          component: ImagePreviewModal,
                          props: {
                            image: src,
                            instructions,
                          },
                          size: 'lg',
                        });

                        switch (response.action) {
                          case 'SET_EXAMPLE':
                            const file = await dataUrlToFile(
                              src,
                              `example-${i}`,
                              image.mimeType,
                            );
                            setValue('example', file);
                            onGenerate();
                            break;
                          case 'SELECT':
                            setValue('selectedImage', i);
                            break;
                          default:
                            break;
                        }
                      },
                    },
                  ],
                };
              })}
              isLoading={isLoading}
              cols={4}
              imageFit="cover"
            />
          </Stack>
        </Stack>
      </form>
    </Modal.Content>
  );
};
